import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@hwm/ui-lib';
import { t, Trans } from '@lingui/macro';
import { Formik, FormikProps } from 'formik';
import { DialogModal } from '../DialogModal';
import { GeneralPreferences } from './GeneralPreferences';
import { GraphPreferences } from './GraphPreferences';
import { PreferencesFormModel } from '@shared/models';
import { mapPreferencesStoreValuesToFrom, preferencesInitialValues } from './Preferences.utils';
import { usePreferencesDataSource } from './preferencesDataSource';
import { isEmpty, isNil, mergeWith } from 'lodash';
import { useAppSelector } from '@core/store/store';
import { selectedAccStateSelector } from '@core/store/library-stores/selectors';
import { SiteListPreferences } from './SiteListPreferences';
import { useNotifications } from '@shared/hooks/useNotifications';
import { preferencesSelector } from '@core/store/entity/preferences/PreferencesSelector';

export const Preferences: React.FC = () => {
  const [showModal, setShowModal] = useState(false);

  const formikRef = useRef<FormikProps<PreferencesFormModel>>(null);

  const { handleError } = useNotifications();

  const { getPreferences, preferencesOptions, getPreferencesOptions, savePreferences } = usePreferencesDataSource();

  const { userRole } = useAppSelector(selectedAccStateSelector) ?? {};
  const { preferences: preferencesValues } = useAppSelector(preferencesSelector);

  const preferencesMergedInitialValues: PreferencesFormModel = useMemo(
    () =>
      preferencesValues
        ? mergeWith(
            mapPreferencesStoreValuesToFrom(preferencesValues),
            preferencesInitialValues,
            (apiVal, defaultVal) => (isNil(apiVal) ? defaultVal : apiVal)
          )
        : preferencesInitialValues,
    [preferencesValues]
  );

  const handleFormSubmit = useCallback(
    (formikRef: React.MutableRefObject<FormikProps<PreferencesFormModel>>) => {
      const formik = formikRef.current;

      formik.submitForm().then(() => {
        const values = formik.values;
        formik.validateForm(values).then((validationErrors) => {
          if (isEmpty(validationErrors)) {
            savePreferences(values, () => {
              setShowModal(false);
              getPreferences();
            });
            return;
          }

          const errors = Object.values(validationErrors).map((val) => val);
          handleError(null, t`Preferences`, errors.join(', '));
        });
      });
    },
    [getPreferences, handleError, savePreferences]
  );

  useEffect(() => {
    if (Boolean(userRole) && userRole !== 'No access') {
      getPreferences();
      getPreferencesOptions();
    }
  }, [userRole, getPreferences, getPreferencesOptions]);

  return userRole === null || userRole === 'No access' ? (
    <></>
  ) : (
    <>
      <Button className="w-full md:w-24" variant="outlined" style={{}} onClick={() => setShowModal(true)}>
        <Trans>Preferences</Trans>
      </Button>
      <DialogModal
        show={showModal}
        setShow={setShowModal}
        actionButtons={[
          <Button onClick={() => handleFormSubmit(formikRef)} style={{}}>
            Save
          </Button>,
        ]}
        headerTitle={t`Preferences`}
      >
        <Formik
          enableReinitialize
          validationSchema={null}
          initialValues={{
            ...preferencesMergedInitialValues,
          }}
          onSubmit={() => {}}
          innerRef={formikRef}
        >
          {({ values, handleSubmit }: FormikProps<PreferencesFormModel>) => {
            return values && !isEmpty(values) ? (
              <form className="flex flex-col md:flex-row" onSubmit={handleSubmit}>
                <div className="w-full md:w-1/2 md:mr-3">
                  <GeneralPreferences
                    conditions={preferencesOptions?.alarmConditions ?? []}
                    exports={preferencesOptions?.exportFormats ?? []}
                  />
                  <SiteListPreferences />
                </div>
                <div className="w-full md:w-1/2 md:ml-3">
                  <GraphPreferences
                    unitPreferencesOptions={preferencesValues?.graphUnitPreferences}
                    graphExports={preferencesOptions?.graphExportFormats ?? []}
                    digitalIntervals={preferencesOptions?.graphDigitalIntervals ?? []}
                    cumulativeModes={preferencesOptions?.graphCumulativeModes ?? []}
                  />
                </div>
              </form>
            ) : null;
          }}
        </Formik>
      </DialogModal>
    </>
  );
};
